/**
 * Drawer — right-anchored slide-out dialog (T8.1a F1).
 *
 * Skórka (wygląd) na wspólnym rdzeniu `Overlay`, jak `Modal`, z różnicami:
 *   - Backdrop: flex justify-end (panel anchored to right edge).
 *   - Panel:    full-height (max-h-screen), no rounded corners on right side.
 *   - `width` prop: 'sm' (domyślnie, 420px — formularze edycji) lub 'half'
 *     (połowa szerokości okna na ekranach ≥sm — wygodny podgląd, np. edytor widgetu).
 *
 * Zachowanie jak w Modal (z `Overlay`): portal, pułapka fokusu, blokada przewijania, Esc,
 * kliknięcie w tło, footer, hideCloseButton. Reused in T8.1b/T8.2/T8.3 for edit forms with many fields.
 */

import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { IconButton } from '@/components/IconButton';
import { Overlay } from '@/components/Overlay';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  hideCloseButton?: boolean;
  /** Szerokość panelu. 'sm' = 420px (domyślnie). 'half' = połowa okna (≥sm). */
  width?: 'sm' | 'half';
  /** `data-testid` na tle okna (zawiera cały panel). */
  testId?: string;
  /** Zabezpieczenie niezapisanych zmian — jak w `Modal` (szczegóły → `Overlay`). */
  guardUnsaved?: boolean;
  /** Jawna informacja „są niezapisane zmiany" (nadpisuje wykrywanie wpisywania). */
  dirty?: boolean;
  /** Zmiana wartości zeruje wykryte „coś wpisano" (okno zostaje otwarte po zapisie). */
  guardResetKey?: string | number;
}

/** Klasy szerokości panelu wg `width`. Na mobile zawsze pełna szerokość. */
const WIDTH_CLASS: Record<NonNullable<DrawerProps['width']>, string> = {
  sm: 'w-full max-w-[420px]',
  half: 'w-full sm:max-w-[50vw]',
};

export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  closeOnBackdrop = true,
  closeOnEsc = true,
  hideCloseButton,
  width = 'sm',
  testId,
  guardUnsaved,
  dirty,
  guardResetKey,
}: DrawerProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();

  return (
    <Overlay
      open={open}
      onClose={onClose}
      closeOnBackdrop={closeOnBackdrop}
      closeOnEsc={closeOnEsc}
      backdropClassName="z-50 flex justify-end bg-black/50"
      panelClassName={[
        'h-full border-l border-border-secondary bg-bg-elevated shadow-xl',
        WIDTH_CLASS[width],
        'flex flex-col max-h-screen',
      ].join(' ')}
      labelledBy={title ? titleId : undefined}
      describedBy={description ? descId : undefined}
      testId={testId}
      guardUnsaved={guardUnsaved}
      dirty={dirty}
      guardResetKey={guardResetKey}
    >
      {(title || !hideCloseButton) && (
        <header className="flex items-start justify-between gap-4 border-b border-border-secondary px-5 py-4">
          <div className="min-w-0">
            {title && (
              <h2 id={titleId} className="text-lg font-semibold text-text-primary">
                {title}
              </h2>
            )}
            {description && (
              <p id={descId} className="mt-1 text-sm text-text-tertiary">
                {description}
              </p>
            )}
          </div>
          {!hideCloseButton && (
            <IconButton
              size="sm"
              variant="ghost"
              onClick={onClose}
              aria-label={t('common.close')}
              className="flex-shrink-0"
            >
              <X aria-hidden="true" />
            </IconButton>
          )}
        </header>
      )}
      <div className="flex-1 overflow-y-auto px-5 py-4 text-sm text-text-primary">{children}</div>
      {footer && (
        <footer className="flex items-center justify-end gap-2 border-t border-border-secondary px-5 py-3">
          {footer}
        </footer>
      )}
    </Overlay>
  );
}
